import React, { useState, useRef, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import styles from "./ExamInfo.module.css";
import { jeeSyllabusData } from "../data/jeeSyllabusData";
import { neetSyllabusData } from "../data/neetSyllabusData";
import SyllabusTable from "../components/SyllabusTable";

// ─── Dynamic year helpers ───────────────────────────────────────────────────
// JEE / NEET cycle: registration opens ~Nov-Feb, exam happens Jan-May.
// Before June  → the upcoming/ongoing exam is THIS calendar year.
// June onwards → focus shifts to NEXT year's cycle.
const _today      = new Date();
const currentYear = _today.getFullYear();
const nextYear    = currentYear + 1;
const examYear    = _today.getMonth() < 5 ? currentYear : nextYear; // 0-based: <5 = before June
const prevYear    = examYear - 1;
const academicYear = `${currentYear}-${String(nextYear).slice(-2)}`;

// ─── Data ───────────────────────────────────────────────────────────────────
const exams = [
  {
    id: "jee-mains",
    tag: "Engineering",
    color: "#2563eb",
    gradient: "linear-gradient(135deg,#1e40af 0%,#3b82f6 100%)",
    icon: "",
    title: "JEE Mains",
    subtitle: `Joint Entrance Examination (Main) ${currentYear}`,
    conducted: "National Testing Agency (NTA)",
    shortDesc: "Gateway to NITs, IIITs, GFTIs and eligibility for JEE Advanced. One of India's most competitive engineering entrance exams.",
    keyFacts: [
      { icon: "", label: "Sessions", value: "January & April" },
      { icon: "", label: "Duration", value: "3 Hours" },
      { icon: "", label: "Mode", value: "Computer Based Test (CBT)" },
      { icon: "", label: "Languages", value: "13 Languages" },
      { icon: "", label: "Eligibility", value: `Class 12 Pass / Appearing (${academicYear})` },
      { icon: "", label: "Attempts", value: "Max 3 consecutive years" },
    ],
    pattern: {
      papers: [
        {
          name: "Paper 1 — B.E. / B.Tech",
          sections: [
            { subject: "Physics", mcq: 20, numerical: 5, marks: "100 (+4 / −1 for both MCQ and Numerical)" },
            { subject: "Chemistry", mcq: 20, numerical: 5, marks: "100 (+4 / −1 for both MCQ and Numerical)" },
            { subject: "Maths", mcq: 20, numerical: 5, marks: "100 (+4 / −1 for both MCQ and Numerical)" },
          ],
          total: "300 Marks | 75 Questions (all compulsory)",
        },
        {
          name: "Paper 2A — B.Arch",
          sections: [
            { subject: "Mathematics", mcq: 20, numerical: 5, marks: "100" },
            { subject: "Aptitude Test", mcq: 50, numerical: 0, marks: "200" },
            { subject: "Drawing Test", mcq: 0, numerical: 2, marks: "100 (offline)" },
          ],
          total: "400 Marks | 77 Questions",
        },
      ],
      markingScheme: "+4 for correct, −1 for wrong, 0 for unattempted — applies to BOTH MCQ and Numerical questions (since 2025). Section B Numericals are now COMPULSORY (no longer 5 of 10).",
      normalization: "Percentile scores calculated separately for each session using the equipercentile method. Best of two sessions considered for final NTA score and ranking.",
      trendCurrent: [
        `${examYear} Update: Section B (Numerical Value) now has only 5 questions per subject — all compulsory. Earlier it was 10 with choice of 5; this changed in JEE Main 2025.`,
        `${examYear} Update: Negative marking (−1) now applies to Numerical Value questions too. Blind attempts in Section B are penalised — accuracy matters more than ever.`,
        "Total questions reduced from 90 (75 attempted) to 75 — all compulsory. Effective per-question time stays ~2.4 minutes.",
        "Difficulty trend: Physics has become moderately calculative; Chemistry remains the highest-scoring (NCERT-line direct); Maths is lengthy with Calculus + Vector-3D + Coordinate carrying the bulk.",
        "PYQ recurrence ~35–45% per shift. Last 5-year shift-wise PYQs are the single highest-ROI practice resource.",
        `Tie-breaking order (${examYear}): Maths percentile → Physics percentile → Chemistry percentile → fewer wrong answers → older candidate.`,
      ],
    },
    syllabus: {
      Physics: [
        "Units and Measurements",
        "Motion in a Straight Line",
        "Motion in a Plane",
        "Laws of Motion",
        "Work, Energy and Power",
        "System of Particles and Rotational Motion",
        "Gravitation",
        "Mechanical Properties of Solids",
        "Mechanical Properties of Fluids",
        "Thermal Properties of Matter",
        "Thermodynamics",
        "Kinetic Theory",
        "Oscillations",
        "Waves",
        "Electric Charges and Fields",
        "Electrostatic Potential and Capacitance",
        "Current Electricity",
        "Moving Charges and Magnetism",
        "Magnetism and Matter",
        "Electromagnetic Induction",
        "Alternating Current",
        "Electromagnetic Waves",
        "Ray Optics and Optical Instruments",
        "Wave Optics",
        "Dual Nature of Radiation and Matter",
        "Atoms",
        "Nuclei",
        "Semiconductor Electronics",
        "Experimental Skills",
      ],
      Chemistry: [
        "Some Basic Concepts of Chemistry",
        "Structure of Atom",
        "Classification of Elements and Periodicity in Properties",
        "Chemical Bonding and Molecular Structure",
        "States of Matter",
        "Thermodynamics",
        "Equilibrium",
        "Redox Reactions",
        "Hydrogen",
        "s-Block Elements",
        "Some p-Block Elements",
        "Organic Chemistry: Some Basic Principles and Techniques",
        "Hydrocarbons",
        "Environmental Chemistry",
        "Solid State",
        "Solutions",
        "Electrochemistry",
        "Chemical Kinetics",
        "Surface Chemistry",
        "General Principles and Processes of Isolation of Elements",
        "The p-Block Elements",
        "The d- and f-Block Elements",
        "Coordination Compounds",
        "Haloalkanes and Haloarenes",
        "Alcohols, Phenols and Ethers",
        "Aldehydes, Ketones and Carboxylic Acids",
        "Amines",
        "Biomolecules",
        "Polymers",
        "Chemistry in Everyday Life",
        "Principles Related to Practical Chemistry",
      ],
      Mathematics: [
        "Sets",
        "Relations and Functions",
        "Trigonometric Functions",
        "Complex Numbers and Quadratic Equations",
        "Linear Inequalities",
        "Permutations and Combinations",
        "Binomial Theorem",
        "Sequence and Series",
        "Straight Lines",
        "Conic Sections",
        "Introduction to Three Dimensional Geometry",
        "Limits and Derivatives",
        "Mathematical Reasoning",
        "Statistics",
        "Probability",
        "Inverse Trigonometric Functions",
        "Matrices",
        "Determinants",
        "Continuity and Differentiability",
        "Application of Derivatives",
        "Integrals",
        "Application of Integrals",
        "Differential Equations",
        "Vector Algebra",
        "Three Dimensional Geometry",
      ],
    },
    importantDates: [
      { event: "Session 1 Registration", date: `Nov ${currentYear}` },
      { event: "Session 1 Exam", date: `Jan ${nextYear}` },
      { event: "Session 2 Registration", date: `Feb ${nextYear}` },
      { event: "Session 2 Exam", date: `Apr ${nextYear}` },
      { event: "Result Declaration", date: `May ${nextYear}` },
    ],
    cutoff: "Typically top ~2.5 lakh students qualify for JEE Advanced.",
    officialSite: "https://jeemain.nta.nic.in/",
    syllabusLink: "/iitjee-syllabus",
    syllabusLinkInternal: true,
    stats: [
      { num: "~11L+", lbl: "Aspirants / Year" },
      { num: "~31,000", lbl: "NIT/IIIT/GFTI Seats" },
      { num: "2", lbl: "Sessions Per Year" },
      { num: academicYear, lbl: "Academic Cycle" },
    ],
  },
  {
    id: "jee-advanced",
    tag: "IIT Admission",
    color: "#7c3aed",
    gradient: "linear-gradient(135deg,#4c1d95 0%,#7c3aed 100%)",
    icon: "",
    title: "JEE Advanced",
    subtitle: `Joint Entrance Examination (Advanced) ${nextYear}`,
    conducted: "IITs (on rotation)",
    shortDesc: "Admission to 23 IITs offering B.Tech, B.S., B.Arch and Integrated programs. Considered one of the toughest exams in the world.",
    keyFacts: [
      { icon: "", label: "Exam Date", value: `May/June ${nextYear}` },
      { icon: "", label: "Duration", value: "3 Hours per paper (2 papers)" },
      { icon: "", label: "Mode", value: "Computer Based Test (CBT)" },
      { icon: "", label: "Eligibility", value: "Top 2.5 lakh JEE Mains qualifiers" },
      { icon: "", label: "Attempts", value: "Max 2 consecutive attempts" },
      { icon: "", label: "Institutes", value: "23 IITs across India" },
    ],
    pattern: {
      papers: [
        {
          name: "Paper 1 (3 Hours)",
          sections: [
            { subject: "Physics", mcq: "~11", numerical: "~6", marks: "~60" },
            { subject: "Chemistry", mcq: "~11", numerical: "~6", marks: "~60" },
            { subject: "Maths", mcq: "~11", numerical: "~6", marks: "~60" },
          ],
          total: "~180 Marks | Mixed question types (MCQ-single, MCQ-multiple, Integer, Match-the-column, Paragraph)",
        },
        {
          name: "Paper 2 (3 Hours)",
          sections: [
            { subject: "Physics", mcq: "~11", numerical: "~6", marks: "~60" },
            { subject: "Chemistry", mcq: "~11", numerical: "~6", marks: "~60" },
            { subject: "Maths", mcq: "~11", numerical: "~6", marks: "~60" },
          ],
          total: "~180 Marks | Mixed question types",
        },
      ],
      markingScheme: "Varies by question type each year — typical scheme: Single-correct MCQ (+3 / −1), Multiple-correct MCQ (+4 / −2 with partial marking), Integer/Numerical (+4 / 0 or +3 / −1), Match-the-column (+3 / −1). Both Paper 1 & Paper 2 are mandatory.",
      normalization: "Aggregate of Paper 1 + Paper 2 used for final ranking (Common Rank List + category lists). No cross-day normalization since exam is held on a single day.",
      trendCurrent: [
        "Paper structure changes year to year — IIT-conducting body redesigns the paper template every cycle. Expect surprises in number of MCQ-multiple, integer, and matrix-match questions.",
        `${examYear} Trend: Increased emphasis on conceptual depth and multi-concept linkage — single-step problems are rare. Each question typically integrates 2–3 concepts.`,
        "Physics: Mechanics + Electrodynamics dominate; expect 2–3 long calculation problems per paper. Modern Physics has become unpredictable but high-yield.",
        "Chemistry: Organic mechanisms (Reagents, named reactions, stereochemistry) and Inorganic (Coordination + Qualitative analysis) lead. Physical Chemistry is data-interpretation heavy.",
        "Maths: Calculus + Coordinate + Vectors-3D form the core. Expect at least one heavy Algebra (Complex/Probability) problem per paper.",
        "Strategy: Master JEE Advanced PYQs of the last 15 years. Pattern changes but topic-wise testing style is remarkably consistent.",
      ],
    },
    syllabus: {
      Physics: ["General Physics (Dimensional Analysis, Errors)", "Mechanics (Laws of Motion, Work, Energy)", "Rotational Mechanics", "Simple Harmonic Motion", "Waves", "Thermal Physics and Thermodynamics", "Electrostatics", "Current Electricity", "Capacitance", "Magnetic Fields and Forces", "Electromagnetic Induction", "Optics (Ray and Wave)", "Modern Physics (Photoelectric, Atoms, Nuclei)"],
      Chemistry: ["General Topics and Mole Concept", "Gaseous and Liquid States", "Atomic Structure and Chemical Bonding", "Energetics and Thermodynamics", "Chemical Equilibrium", "Electrochemistry", "Chemical Kinetics", "Solid State and Solutions", "Surface Chemistry", "Isolation of Metals", "p-Block and d-Block Elements", "Coordination Compounds", "Organic Chemistry (Halides, Alcohols, Ethers)", "Aldehydes and Ketones", "Carboxylic Acids and Amines", "Practical Organic Chemistry"],
      Mathematics: ["Algebra (Complex Numbers, Quadratic Equations)", "Matrices and Determinants", "Probability and Statistics", "Trigonometry and Inverse Trig", "Analytical Geometry (2D and 3D)", "Differential Calculus", "Integral Calculus", "Vectors", "Permutations and Combinations", "Binomial Theorem"],
    },
    importantDates: [
      { event: "Registration Opens", date: `Apr ${nextYear}` },
      { event: "Admit Card", date: `May ${nextYear}` },
      { event: "Paper 1 & 2", date: `May ${nextYear}` },
      { event: "Answer Key Release", date: `June ${nextYear}` },
      { event: "Result Declaration", date: `June ${nextYear}` },
    ],
    cutoff: `Category-wise cutoffs vary. General category typically needs ~35— E0% aggregate. Ranks released for top ~40,000 students.`,
    officialSite: "https://jeeadv.ac.in",
    syllabusLink: "/iitjee-syllabus",
    syllabusLinkInternal: true,
    stats: [
      { num: "~2.5L", lbl: "Qualify from JEE Mains" },
      { num: "~17,000", lbl: "IIT Seats" },
      { num: "23", lbl: "IITs across India" },
      { num: academicYear, lbl: "Academic Cycle" },
    ],
  },
  {
    id: "neet",
    tag: "Medical",
    color: "#059669",
    gradient: "linear-gradient(135deg,#064e3b 0%,#059669 100%)",
    icon: "",
    title: "NEET UG",
    subtitle: `National Eligibility cum Entrance Test (UG) ${nextYear}`,
    conducted: "National Testing Agency (NTA)",
    shortDesc: "Single national entrance test for admission to MBBS, BDS, BAMS, BSMS, BUMS, BHMS and BSc Nursing courses across India.",
    keyFacts: [
      { icon: "", label: "Exam Date", value: `May ${nextYear} (1st Sunday)` },
      { icon: "", label: "Duration", value: "3 Hours (Section B removed since 2025)" },
      { icon: "", label: "Mode", value: "Pen & Paper (OMR Based)" },
      { icon: "", label: "Languages", value: "13 Languages" },
      { icon: "", label: "Eligibility", value: `Class 12 with PCB (min 50%). Age 17+ by Dec 31 ${nextYear}` },
      { icon: "", label: "Attempts", value: "No limit (as per current rules)" },
    ],
    pattern: {
      papers: [
        {
          name: "NEET UG Single Paper (3 Hours)",
          sections: [
            { subject: "Physics", mcq: 45, numerical: 0, marks: "180" },
            { subject: "Chemistry", mcq: 45, numerical: 0, marks: "180" },
            { subject: "Botany", mcq: 45, numerical: 0, marks: "180" },
            { subject: "Zoology", mcq: 45, numerical: 0, marks: "180" },
          ],
          total: "720 Marks | 180 Questions",
        },
      ],
      markingScheme: "+4 for correct answer, −1 for wrong answer, 0 for unattempted. No partial marking. Heavy negative-marking discipline required.",
      normalization: "No normalization — single nationwide paper, single shift. Direct raw marks used for All-India merit list and tie-breaking.",
      trendCurrent: [
        `${examYear} Update: Section B (15 questions, attempt any 10) has been REMOVED. The paper is now 180 questions all compulsory, 720 marks, 3 hours duration (was 3 hours 20 minutes earlier).`,
        "Per-question time reduced to ~60 seconds — speed and accuracy are equally critical now.",
        "Biology = 50% of the paper (360/720 marks). Genetics + Human Physiology + Cell Biology + Ecology together carry ~60% of Biology marks.",
        "~85–90% of NEET Biology questions are direct or paraphrased from NCERT lines, diagrams, tables and examples. NCERT line-by-line revision is the highest-ROI activity.",
        "Chemistry: Inorganic (~12 Q) is the safest scoring block — fully NCERT-driven. Physical (~12 Q) is formula-heavy. Organic (~12 Q) tests reaction mechanisms and named reactions.",
        "Physics: Mechanics + Electrodynamics + Modern Physics + Heat & Optics = ~76% of Physics paper. Pure formula-application style; low derivation depth.",
        `Tie-breaking order (${examYear}): Biology marks → Chemistry marks → Physics marks → fewer wrong answers → older candidate. (Age criterion was reinstated in 2024.)`,
      ],
    },
    syllabus: {
      Physics: [
        "Units and Measurements",
        "Motion in a Straight Line",
        "Motion in a Plane",
        "Laws of Motion",
        "Work, Energy and Power",
        "System of Particles and Rotational Motion",
        "Gravitation",
        "Mechanical Properties of Solids",
        "Mechanical Properties of Fluids",
        "Thermal Properties of Matter",
        "Thermodynamics",
        "Kinetic Theory",
        "Oscillations",
        "Waves",
        "Electric Charges and Fields",
        "Electrostatic Potential and Capacitance",
        "Current Electricity",
        "Moving Charges and Magnetism",
        "Magnetism and Matter",
        "Electromagnetic Induction",
        "Alternating Current",
        "Electromagnetic Waves",
        "Ray Optics and Optical Instruments",
        "Wave Optics",
        "Dual Nature of Radiation and Matter",
        "Atoms",
        "Nuclei",
        "Semiconductor Electronics",
        "Experimental Skills",
      ],
      Chemistry: [
        "Some Basic Concepts of Chemistry",
        "Structure of Atom",
        "Classification of Elements and Periodicity in Properties",
        "Chemical Bonding and Molecular Structure",
        "States of Matter",
        "Thermodynamics",
        "Equilibrium",
        "Redox Reactions",
        "Hydrogen",
        "s-Block Elements",
        "Some p-Block Elements",
        "Organic Chemistry: Some Basic Principles and Techniques",
        "Hydrocarbons",
        "Environmental Chemistry",
        "Solid State",
        "Solutions",
        "Electrochemistry",
        "Chemical Kinetics",
        "Surface Chemistry",
        "General Principles and Processes of Isolation of Elements",
        "The p-Block Elements",
        "The d- and f-Block Elements",
        "Coordination Compounds",
        "Haloalkanes and Haloarenes",
        "Alcohols, Phenols and Ethers",
        "Aldehydes, Ketones and Carboxylic Acids",
        "Amines",
        "Biomolecules",
        "Polymers",
        "Chemistry in Everyday Life",
        "Principles Related to Practical Chemistry",
      ],
      Biology: [
        "The Living World",
        "Biological Classification",
        "Plant Kingdom",
        "Animal Kingdom",
        "Morphology of Flowering Plants",
        "Anatomy of Flowering Plants",
        "Structural Organisation in Animals",
        "Cell: The Unit of Life",
        "Biomolecules",
        "Cell Cycle and Cell Division",
        "Transport in Plants",
        "Mineral Nutrition",
        "Photosynthesis in Higher Plants",
        "Respiration in Plants",
        "Plant Growth and Development",
        "Digestion and Absorption",
        "Breathing and Exchange of Gases",
        "Body Fluids and Circulation",
        "Excretory Products and Their Elimination",
        "Locomotion and Movement",
        "Neural Control and Coordination",
        "Chemical Coordination and Integration",
        "Reproduction in Organisms",
        "Sexual Reproduction in Flowering Plants",
        "Human Reproduction",
        "Reproductive Health",
        "Principles of Inheritance and Variation",
        "Molecular Basis of Inheritance",
        "Evolution",
        "Human Health and Disease",
        "Microbes in Human Welfare",
        "Biotechnology: Principles and Processes",
        "Biotechnology and Its Applications",
        "Organisms and Populations",
        "Ecosystem",
        "Biodiversity and Conservation",
        "Environmental Issues",
      ],
    },
    importantDates: [
      { event: "Registration Opens", date: `Feb ${nextYear}` },
      { event: "Last Date to Apply", date: `Mar ${nextYear}` },
      { event: "Admit Card Download", date: `May ${nextYear}` },
      { event: "Exam Date", date: `May ${nextYear} (1st Sunday)` },
      { event: "Result Declaration", date: `June ${nextYear}` },
    ],
    cutoff: "General category cutoff typically ~720— E30. For MBBS govt colleges expect 550+. State quotas have separate cutoffs.",
    officialSite: "https://neet.nta.nic.in",
    syllabusLink: "/neet-syllabus",
    syllabusLinkInternal: true,
    stats: [
      { num: "~22L+", lbl: "Aspirants / Year" },
      { num: "~1.08L", lbl: "MBBS Seats" },
      { num: "1", lbl: "Session Per Year" },
      { num: academicYear, lbl: "Academic Cycle" },
    ],
  },
];

const comparisonData = [
  { param: "Conducting Body", jeeMain: "NTA (https://nta.nic.in/)", jeeAdv: "IITs", neet: "NTA (https://nta.nic.in/)" },
  { param: "Exam Mode", jeeMain: "CBT (Online)", jeeAdv: "CBT (Online)", neet: "OMR (Offline)" },
  { param: "Frequency", jeeMain: "Twice a year", jeeAdv: "Once a year", neet: "Once a year" },
  { param: "Duration", jeeMain: "3 Hours", jeeAdv: "3+3 Hours", neet: "3 Hours" },
  { param: "Total Marks", jeeMain: "300", jeeAdv: "~360", neet: "720" },
  { param: "Negative Marking", jeeMain: "−1 (MCQ)", jeeAdv: "Varies (−1 to −1)", neet: "−1" },
  { param: "Admission To", jeeMain: "NITs, IIITs, GFTIs", jeeAdv: "IITs", neet: "MBBS, BDS, BAMS etc." },
  { param: `Seats (Approx ${nextYear})`, jeeMain: "~31,000", jeeAdv: "~17,000", neet: "~1,08,000 MBBS" },
];

const strategyDurationOptions = [
  { id: "lm-rev", label: "Last Month Revision Plan" },
  { id: "6m-prep", label: "6 Month Preparation Plan" },
  { id: "6m-rev", label: "6 Month Revision Plan" },
  { id: "1y-prep", label: "1 Year Preparation Plan" },
  { id: "1y-rev", label: "1 Year Revision Plan" },
  { id: "2y-prep", label: "2 Year Preparation Plan" },
];

const strategyDurationPlanType = {
  "lm-rev": "revision",
  "6m-prep": "prep",
  "6m-rev": "revision",
  "1y-prep": "prep",
  "1y-rev": "revision",
  "2y-prep": "prep",
};

// Trend insights backed by NTA released response sheets and chapter-wise PYQ counts (last 5 years average).
const strategyTrendInsights = {
  jee: [
    "Physics: Mechanics (~22%) + Electrodynamics i.e. Current Electricity + EMI + AC (~20%) + Modern Physics (~14%) cover ~56% of the paper. Master these three blocks first — they decide selection.",
    "Chemistry: Physical, Inorganic and Organic each contribute ~33% of the paper. Inorganic (Bonding, p/d/f-block, Coordination) is the highest accuracy zone — fully NCERT-driven and 100% solvable.",
    "Maths: Calculus (~28%) + Algebra (Complex, Quadratic, Sequence, Binomial, P&C, Probability — together ~24%) + Coordinate Geometry (~14%) + Vector–3D (~12%) = ~78% of the paper. Speed in these decides percentile.",
    "PYQ recurrence: ~35–45% of every JEE Main shift question pattern is directly traceable to last 10-year PYQs. Exhaustive PYQ practice is non-negotiable.",
  ],
  neet: [
    "Biology = 50% of the paper. Genetics & Evolution (~20%) + Human Physiology (~18%) + Cell Biology (~11%) + Ecology (~11%) = ~60% of Biology marks. Biology decides the rank, not Physics.",
    "NEET Bio NCERT-line accuracy: ~85–90% of NEET Biology questions are direct or paraphrased from NCERT lines, diagrams, and tables. NCERT line-by-line revision is the single highest-ROI activity.",
    "Chemistry: Inorganic (Bonding, Coordination, p-block, Periodicity) ~12 questions, Physical (Equilibrium, Thermodynamics, Electrochemistry, Solutions) ~12, Organic (GOC, Hydrocarbons, Biomolecules, Aldehydes/Acids/Amines, Alcohols) ~12. Inorganic is the safest scoring block.",
    "Physics: Mechanics (~28%) + Electrodynamics (~14%) + Modern Physics (~13%) + Heat & Thermodynamics (~11%) + Optics (~10%) = ~76% of the paper. Pure formula-application, low derivation depth.",
  ],
};

const strategySyllabusByExam = {
  jee: exams.find((e) => e.id === "jee-mains")?.syllabus || {},
  neet: exams.find((e) => e.id === "neet")?.syllabus || {},
};

// Explicit chapter -> priority map, derived from NTA chapter-wise PYQ frequency (last 5 years).
// HP = consistently 3+ questions/year (must master); MP = 1-2 questions/year (cover after HP);
// LP = <1 question/year average (skim only after HP+MP are solid).
const strategyChapterPriority = {
  jee: {
    Physics: {
      "Units and Measurements": "LP",
      "Motion in a Straight Line": "HP",
      "Motion in a Plane": "HP",
      "Laws of Motion": "HP",
      "Work, Energy and Power": "HP",
      "System of Particles and Rotational Motion": "HP",
      "Gravitation": "MP",
      "Mechanical Properties of Solids": "LP",
      "Mechanical Properties of Fluids": "MP",
      "Thermal Properties of Matter": "MP",
      "Thermodynamics": "HP",
      "Kinetic Theory": "MP",
      "Oscillations": "HP",
      "Waves": "MP",
      "Electric Charges and Fields": "HP",
      "Electrostatic Potential and Capacitance": "HP",
      "Current Electricity": "HP",
      "Moving Charges and Magnetism": "HP",
      "Magnetism and Matter": "MP",
      "Electromagnetic Induction": "HP",
      "Alternating Current": "HP",
      "Electromagnetic Waves": "LP",
      "Ray Optics and Optical Instruments": "HP",
      "Wave Optics": "HP",
      "Dual Nature of Radiation and Matter": "HP",
      "Atoms": "HP",
      "Nuclei": "HP",
      "Semiconductor Electronics": "HP",
      "Experimental Skills": "LP",
    },
    Chemistry: {
      "Some Basic Concepts of Chemistry": "HP",
      "Structure of Atom": "HP",
      "Classification of Elements and Periodicity in Properties": "HP",
      "Chemical Bonding and Molecular Structure": "HP",
      "States of Matter": "MP",
      "Thermodynamics": "HP",
      "Equilibrium": "HP",
      "Redox Reactions": "MP",
      "Hydrogen": "LP",
      "s-Block Elements": "MP",
      "Some p-Block Elements": "MP",
      "Organic Chemistry: Some Basic Principles and Techniques": "HP",
      "Hydrocarbons": "HP",
      "Environmental Chemistry": "LP",
      "Solid State": "HP",
      "Solutions": "HP",
      "Electrochemistry": "HP",
      "Chemical Kinetics": "HP",
      "Surface Chemistry": "MP",
      "General Principles and Processes of Isolation of Elements": "LP",
      "The p-Block Elements": "HP",
      "The d- and f-Block Elements": "HP",
      "Coordination Compounds": "HP",
      "Haloalkanes and Haloarenes": "HP",
      "Alcohols, Phenols and Ethers": "HP",
      "Aldehydes, Ketones and Carboxylic Acids": "HP",
      "Amines": "HP",
      "Biomolecules": "HP",
      "Polymers": "MP",
      "Chemistry in Everyday Life": "LP",
      "Principles Related to Practical Chemistry": "LP",
    },
    Mathematics: {
      "Sets": "LP",
      "Relations and Functions": "MP",
      "Trigonometric Functions": "MP",
      "Complex Numbers and Quadratic Equations": "HP",
      "Linear Inequalities": "LP",
      "Permutations and Combinations": "HP",
      "Binomial Theorem": "HP",
      "Sequence and Series": "HP",
      "Straight Lines": "MP",
      "Conic Sections": "HP",
      "Introduction to Three Dimensional Geometry": "MP",
      "Limits and Derivatives": "MP",
      "Mathematical Reasoning": "LP",
      "Statistics": "LP",
      "Probability": "HP",
      "Inverse Trigonometric Functions": "MP",
      "Matrices": "HP",
      "Determinants": "HP",
      "Continuity and Differentiability": "HP",
      "Application of Derivatives": "HP",
      "Integrals": "HP",
      "Application of Integrals": "MP",
      "Differential Equations": "MP",
      "Vector Algebra": "HP",
      "Three Dimensional Geometry": "HP",
    },
  },
  neet: {
    Physics: {
      "Units and Measurements": "LP",
      "Motion in a Straight Line": "HP",
      "Motion in a Plane": "HP",
      "Laws of Motion": "HP",
      "Work, Energy and Power": "HP",
      "System of Particles and Rotational Motion": "HP",
      "Gravitation": "MP",
      "Mechanical Properties of Solids": "LP",
      "Mechanical Properties of Fluids": "MP",
      "Thermal Properties of Matter": "MP",
      "Thermodynamics": "HP",
      "Kinetic Theory": "MP",
      "Oscillations": "MP",
      "Waves": "MP",
      "Electric Charges and Fields": "HP",
      "Electrostatic Potential and Capacitance": "HP",
      "Current Electricity": "HP",
      "Moving Charges and Magnetism": "HP",
      "Magnetism and Matter": "MP",
      "Electromagnetic Induction": "MP",
      "Alternating Current": "MP",
      "Electromagnetic Waves": "LP",
      "Ray Optics and Optical Instruments": "HP",
      "Wave Optics": "HP",
      "Dual Nature of Radiation and Matter": "HP",
      "Atoms": "HP",
      "Nuclei": "HP",
      "Semiconductor Electronics": "HP",
      "Experimental Skills": "LP",
    },
    Chemistry: {
      "Some Basic Concepts of Chemistry": "HP",
      "Structure of Atom": "HP",
      "Classification of Elements and Periodicity in Properties": "HP",
      "Chemical Bonding and Molecular Structure": "HP",
      "States of Matter": "MP",
      "Thermodynamics": "HP",
      "Equilibrium": "HP",
      "Redox Reactions": "MP",
      "Hydrogen": "LP",
      "s-Block Elements": "MP",
      "Some p-Block Elements": "MP",
      "Organic Chemistry: Some Basic Principles and Techniques": "HP",
      "Hydrocarbons": "HP",
      "Environmental Chemistry": "LP",
      "Solid State": "HP",
      "Solutions": "HP",
      "Electrochemistry": "HP",
      "Chemical Kinetics": "HP",
      "Surface Chemistry": "MP",
      "General Principles and Processes of Isolation of Elements": "LP",
      "The p-Block Elements": "HP",
      "The d- and f-Block Elements": "HP",
      "Coordination Compounds": "HP",
      "Haloalkanes and Haloarenes": "HP",
      "Alcohols, Phenols and Ethers": "HP",
      "Aldehydes, Ketones and Carboxylic Acids": "HP",
      "Amines": "HP",
      "Biomolecules": "HP",
      "Polymers": "MP",
      "Chemistry in Everyday Life": "LP",
      "Principles Related to Practical Chemistry": "LP",
    },
    Biology: {
      "The Living World": "MP",
      "Biological Classification": "MP",
      "Plant Kingdom": "MP",
      "Animal Kingdom": "HP",
      "Morphology of Flowering Plants": "MP",
      "Anatomy of Flowering Plants": "MP",
      "Structural Organisation in Animals": "MP",
      "Cell: The Unit of Life": "HP",
      "Biomolecules": "HP",
      "Cell Cycle and Cell Division": "HP",
      "Transport in Plants": "MP",
      "Mineral Nutrition": "LP",
      "Photosynthesis in Higher Plants": "HP",
      "Respiration in Plants": "MP",
      "Plant Growth and Development": "MP",
      "Digestion and Absorption": "HP",
      "Breathing and Exchange of Gases": "HP",
      "Body Fluids and Circulation": "HP",
      "Excretory Products and Their Elimination": "HP",
      "Locomotion and Movement": "MP",
      "Neural Control and Coordination": "HP",
      "Chemical Coordination and Integration": "HP",
      "Reproduction in Organisms": "LP",
      "Sexual Reproduction in Flowering Plants": "HP",
      "Human Reproduction": "HP",
      "Reproductive Health": "MP",
      "Principles of Inheritance and Variation": "HP",
      "Molecular Basis of Inheritance": "HP",
      "Evolution": "MP",
      "Human Health and Disease": "HP",
      "Microbes in Human Welfare": "MP",
      "Biotechnology: Principles and Processes": "HP",
      "Biotechnology and Its Applications": "HP",
      "Organisms and Populations": "MP",
      "Ecosystem": "HP",
      "Biodiversity and Conservation": "MP",
      "Environmental Issues": "LP",
    },
  },
};

// Universal topper principles distilled from AIR 1-100 interviews and learning-science research.
const strategyCorePrinciples = [
  {
    title: "PYQ-First Learning",
    detail: "After every chapter, immediately solve last 10 years of PYQs of that chapter (chapterwise PYQ books). ~35-45% of JEE and ~50% of NEET concepts repeat in pattern. PYQs reveal exactly how a concept is asked.",
  },
  {
    title: "Spaced Repetition (1-3-7-15-30)",
    detail: "Every newly studied chapter must be revised after 1 day, 3 days, 7 days, 15 days, and 30 days. Without this loop, retention drops to ~20% in a month. Maintain a simple revision tracker per chapter.",
  },
  {
    title: "Active Recall over Re-reading",
    detail: "Close the book after studying and write everything you remember on a blank page (the 'blurting' method). Then check gaps. Active recall is 2-3x more effective than passive re-reading or highlighting.",
  },
  {
    title: "One Resource per Subject + PYQ",
    detail: "Pick one main book/module per subject and finish it cover-to-cover before adding a second. Resource jumping is the #1 cause of mid-year burnout. NCERT is non-negotiable for both exams.",
  },
  {
    title: "70-30 Weak vs Strong",
    detail: "Spend 70% of study time on chapters where accuracy is below 70%, and 30% maintaining strong chapters. Track accuracy chapterwise after every test — your error log is your truest syllabus.",
  },
  {
    title: "Daily 3-Subject Rotation",
    detail: "Touch all 3 (JEE) or all 3 (NEET — Bio counts as one) subjects every single day. Single-subject days break recency and retention. Keep the toughest subject in your peak focus window.",
  },
  {
    title: "Sleep + Health are Performance Levers",
    detail: "Sleep 7-8 hours non-negotiable — memory consolidation happens in REM. 30 min/day exercise. No phone in study room. Toppers are not sleep-deprived; they are time-disciplined.",
  },
  {
    title: "Test = Learning, Not Measurement",
    detail: "Treat every chapter test, sectional, and full mock as a learning event. The test that exposes 5 weak topics is more valuable than 3 days of theory.",
  },
];

// Mock test analysis methodology — single biggest score multiplier in last 6 months.
const strategyMockProtocol = [
  {
    title: "2x Analysis Rule",
    detail: "Spend 2x the test duration on analysis. A 3-hour mock = 6 hours of deep analysis. No new mock until the previous one is fully analysed.",
  },
  {
    title: "Categorize Every Wrong / Skipped Question",
    detail: "Tag each error as: (1) Concept gap, (2) Application/calculation error, (3) Silly mistake, (4) Time pressure, (5) Misread question. The category drives the fix.",
  },
  {
    title: "Re-attempt within 24 Hours",
    detail: "Re-solve every wrong and every skipped question within 24 hours, untimed. If you still cannot solve, go back to theory of that micro-topic immediately — that is your real syllabus gap.",
  },
  {
    title: "Maintain a Living Error Notebook",
    detail: "One notebook per subject. Write the question stem, your wrong attempt, the correct logic, and the underlying concept in one line. Revise this notebook every Sunday and entirely in the last 15 days.",
  },
  {
    title: "Negative-Marking Discipline",
    detail: "JEE: attempt only if you are ≥75% sure. NEET: attempt only if ≥80% sure (negative is 1/4 not 1/3 — guess-cost is high). Track 'attempted but wrong' per mock — bring it under 10%.",
  },
  {
    title: "Mock Frequency Ladder",
    detail: "Months 1-3: 1 chapter test/week. Months 4-5: 2 sectional + 1 full mock/week. Final month: 3-4 full mocks/week, exact exam slot timing, OMR for NEET, computer interface for JEE.",
  },
];

const strategyPitfalls = [
  "Studying for hours but not testing — test daily, even 30 questions counts.",
  "Resource hoarding (3+ books per subject) — finish one before starting another.",
  "Skipping NCERT for 'advanced' books — NEET especially loses 60+ marks this way.",
  "Doing mocks but not analysing — an unanalysed mock is wasted time.",
  "Single-subject days / weeks — recency effect destroys retention.",
  "Last-minute new chapters in final 30 days — strictly revise only.",
  "No error notebook — same mistake repeats across mocks invisibly.",
  "Sleep deprivation as 'dedication' — drops cognition, increases silly errors.",
  "Comparing test scores instead of trend — track your own accuracy graph.",
  "Ignoring mental health and breaks — 6 days study + 1 day light recovery is the proven cycle.",
];

const strategyLast30Days = {
  jee: [
    "STRICT NO new chapter. Only revision + tests + analysis.",
    "Daily: 60 min formula sheet revision (Physics + Maths) + 60 min reaction/named-reaction sheet (Chemistry).",
    "Mock cadence: 3-4 full mocks/week, exact JEE slot (9 AM or 3 PM), computer interface practice.",
    "After every mock: 4-6 hours analysis with error categorization. Re-solve all wrong/skipped within 24h.",
    "Last 7 days: only revision of error notebook + 2 mocks total. No new resource. Sleep at exam-time schedule.",
    "Exam-day strategy: 2-pass approach. First pass = sure questions only (skip if >2 min). Second pass = re-attempt skipped. Attempt-only-if-≥75%-sure rule.",
  ],
  neet: [
    "STRICT NO new chapter. NCERT line revision + PYQ + mocks.",
    "Daily: 2-3 NCERT chapters of Biology line-by-line revision (rotate so all 38 covered in ~15 days twice).",
    "Daily: 100 Biology MCQs + 40 Physics + 40 Chemistry minimum. Track accuracy chapterwise.",
    "Mock cadence: 3-4 full mocks/week on actual OMR sheet, 2 PM start (NEET slot). Practice bubble-filling in batches of 10.",
    "After every mock: error log with NCERT line reference for every wrong Biology/Chemistry question.",
    "Last 7 days: only NCERT + error notebook + 2 mocks. Sleep, light food, exam-time wake routine.",
    "Exam-day: ≥80% sure rule for marking. Section B — pick the easiest 10 of 15 first, mark only confident ones in OMR.",
  ],
};

const strategyDurationMeta = {
  "lm-rev":  { table1Title: "Last Month Revision Plan (4 Weeks · High-Yield)",          table1Focus: "Mixed HP revision + PYQs + error elimination" },
  "6m-prep": { table1Title: "6 Month Preparation Plan (Learn → Revise → Mock)",         table1Focus: "Full syllabus + first revision + mock simulation" },
  "6m-rev":  { table1Title: "6 Month Revision Plan (Slow Pass + Fast Pass + Mocks)",    table1Focus: "Every chapter revised twice + mock-driven repair" },
  "1y-prep": { table1Title: "1 Year Preparation Plan (Learn → Slow Rev → Exam Mode)",   table1Focus: "Full syllabus + 2 revisions + 4 months of mocks" },
  "1y-rev":  { table1Title: "1 Year Revision Plan (Slow + Fast + Mock Marathon)",       table1Focus: "Every chapter twice + 5-month mock marathon" },
  "2y-prep": { table1Title: "2 Year Preparation Plan (Class 11 → Class 12 → Exam)",     table1Focus: "Class 11 first → Class 12 next → full revision before exam" },
};

// Class 11 chapter count per subject (NCERT order). Used to split the syllabus
// into Year-1 (Class 11) and Year-2 (Class 12) for the 2-year plan.
const class11ChapterCounts = {
  jee:  { Physics: 14, Chemistry: 14, Mathematics: 15 },
  neet: { Physics: 14, Chemistry: 14, Biology: 15 },
};

const chunkEvenly = (items, parts) => {
  const chunks = [];
  let start = 0;

  for (let i = 0; i < parts; i += 1) {
    const size = Math.ceil((items.length - start) / (parts - i));
    chunks.push(items.slice(start, start + size));
    start += size;
  }

  return chunks;
};

const chapterPriority = (examKey, subject, chapter) => {
  return strategyChapterPriority[examKey]?.[subject]?.[chapter] || "MP";
};

const sortByPriority = (items) => [
  ...items.filter((it) => it.priority === "HP"),
  ...items.filter((it) => it.priority === "MP"),
  ...items.filter((it) => it.priority === "LP"),
];

const filterByClass = (items, klass, subject, examKey) => {
  if (!klass) return items;
  const cutoff = class11ChapterCounts[examKey]?.[subject] || Math.ceil(items.length / 2);
  return klass === 11 ? items.slice(0, cutoff) : items.slice(cutoff);
};

// Build chapter-distribution rows for Table 1.
//   - PREP plans: distribute every chapter ONCE across all months (sequential learning).
//   - REVISION plans (and any stage with chapterClass): per-stage distribution
//     so EVERY chapter is touched within EACH stage (Slow Rev pass, Fast Rev pass, Mock pass).
const buildStrategyRowsByDuration = (examKey, durationKey) => {
  const syllabus = strategySyllabusByExam[examKey];
  const subjects = Object.keys(syllabus);
  const phases = strategyRoadmap[examKey][durationKey].phases;
  const stages = strategyStages[examKey]?.[durationKey] || [{ from: 0 }];

  const stageRanges = stages.map((s, i) => ({
    ...s,
    start: s.from,
    end: stages[i + 1]?.from ?? phases.length,
  }));

  const orderedBySubject = {};
  subjects.forEach((subject) => {
    orderedBySubject[subject] = (syllabus[subject] || []).map((chapter) => ({
      chapter,
      priority: chapterPriority(examKey, subject, chapter),
    }));
  });

  // ALWAYS distribute per-stage. Each stage independently spreads its full
  // (filtered) chapter pool across its own months — so a "Slow Revision" stage
  // of 2 months will cover EVERY chapter in those 2 months, not just a slice.
  const stageChunks = {};
  subjects.forEach((subject) => {
    stageChunks[subject] = stageRanges.map((s) =>
      chunkEvenly(
        sortByPriority(filterByClass(orderedBySubject[subject], s.chapterClass, subject, examKey)),
        Math.max(1, s.end - s.start),
      ),
    );
  });

  return phases.map((phase, idx) => {
    const row = { phase: phase.label, extra: phase.extra };
    const stageIdx = Math.max(0, stageRanges.findIndex((r) => idx >= r.start && idx < r.end));
    const offsetInStage = idx - stageRanges[stageIdx].start;
    subjects.forEach((subject) => {
      const chunk = stageChunks[subject][stageIdx]?.[offsetInStage] || [];
      row[subject] = chunk.map((it) => `${it.chapter} [${it.priority}]`).join("\n");
    });
    return row;
  });
};

// ─── Stage banners (Big-picture milestones inside Table 1) ──────────────
// `from` = phase index where this stage starts (banner appears above that row).
// `chapterClass` (optional) = restrict the chapter pool to Class 11 or 12 only
// for that stage's months (used by the 2-year plan).
const strategyStages = {
  jee: {
    "lm-rev": [
      { from: 0, title: " STAGE 1 — REVIVE",   subtitle: "Quick HP refresh + chapter PYQ blocks (Wk 1)" },
      { from: 1, title: " STAGE 2 — REPAIR",   subtitle: "Fix weak topics + ramp up mocks (Wk 2)" },
      { from: 2, title: " STAGE 3 — SIMULATE", subtitle: "Full-paper rhythm + final polish (Wk 3-4)" },
    ],
    "6m-prep": [
      { from: 0, title: " STAGE 1 — LEARN",            subtitle: "Cover the entire syllabus once: theory + examples + DPP (M1-M4)" },
      { from: 4, title: " STAGE 2 — FIRST REVISION",   subtitle: "Syllabus done. Now redo every chapter with PYQs (M5)" },
      { from: 5, title: " STAGE 3 — EXAM SIMULATION",  subtitle: "Full mocks + error fixing (M6)" },
    ],
    "6m-rev": [
      { from: 0, title: " STAGE 1 — SLOW REVISION (deep)",      subtitle: "Re-read theory + redo all PYQs across full syllabus (M1-M2)" },
      { from: 2, title: " STAGE 2 — FAST REVISION + WEAK FIX",  subtitle: "Slow done. Speed up + repair weak chapters (M3-M4)" },
      { from: 4, title: " STAGE 3 — MOCK MODE",                 subtitle: "Mock-driven tuning + final precision cycle (M5-M6)" },
    ],
    "1y-prep": [
      { from: 0,  title: " STAGE 1 — LEARN (full syllabus)", subtitle: "Concept-first chapter coverage of every PCM chapter (M1-M8)" },
      { from: 8,  title: " STAGE 2 — SLOW REVISION",         subtitle: "Syllabus done. First deep revision of every chapter (M9-M10)" },
      { from: 10, title: " STAGE 3 — EXAM MODE",             subtitle: "Fast revision + 2-3 mocks/wk + sheets (M11-M12)" },
    ],
    "1y-rev": [
      { from: 0, title: " STAGE 1 — SLOW REVISION (deep)",     subtitle: "Re-read theory + redo all PYQs every chapter (M1-M4)" },
      { from: 4, title: " STAGE 2 — FAST REVISION + WEAK FIX", subtitle: "Slow done. Condensed notes + speed PYQs every chapter (M5-M7)" },
      { from: 7, title: " STAGE 3 — MOCK MARATHON",            subtitle: "Full mocks + mock-driven weak repair (M8-M12)" },
    ],
    "2y-prep": [
      { from: 0,  title: " YEAR 1 — CLASS 11 LEARN",     subtitle: "Build Class 11 base across PCM (M1-M10)", chapterClass: 11 },
      { from: 10, title: " YEAR 1 END — CLASS 11 REV",   subtitle: "Class 11 SLOW revision pass + first mocks (M11-M12)", chapterClass: 11 },
      { from: 12, title: " YEAR 2 — CLASS 12 LEARN",     subtitle: "Class 12 chapters + Class 11 maintenance via mocks (M13-M20)", chapterClass: 12 },
      { from: 20, title: " STAGE 4 — FULL SYLLABUS REV", subtitle: "Both classes: Slow + Fast revision pass (M21-M22)" },
      { from: 22, title: " STAGE 5 — EXAM MODE",         subtitle: "Mocks + sheets + final precision (M23-M24)" },
    ],
  },
  neet: {
    "lm-rev": [
      { from: 0, title: " STAGE 1 — REVIVE NCERT", subtitle: "Biology line-by-line + HP formulas (Wk 1)" },
      { from: 1, title: " STAGE 2 — REPAIR",       subtitle: "Physics weak topics + Chemistry drill (Wk 2)" },
      { from: 2, title: " STAGE 3 — SIMULATE",     subtitle: "Full-paper rhythm + OMR conditioning (Wk 3-4)" },
    ],
    "6m-prep": [
      { from: 0, title: " STAGE 1 — LEARN",            subtitle: "Cover Bio + Chem + Phy chapter by chapter with NCERT (M1-M4)" },
      { from: 4, title: " STAGE 2 — FIRST REVISION",   subtitle: "Syllabus done. Redo every chapter with NCERT recap + PYQs (M5)" },
      { from: 5, title: " STAGE 3 — EXAM SIMULATION",  subtitle: "Full mocks + OMR speed + error fixing (M6)" },
    ],
    "6m-rev": [
      { from: 0, title: " STAGE 1 — SLOW REVISION (NCERT-deep)", subtitle: "Re-read NCERT + redo PYQs across full PCB (M1-M2)" },
      { from: 2, title: " STAGE 2 — FAST REVISION + WEAK FIX",  subtitle: "Slow done. Speed up + recall-heavy Bio + formula drill (M3-M4)" },
      { from: 4, title: " STAGE 3 — MOCK MODE",                 subtitle: "Mock-driven tuning + final precision cycle (M5-M6)" },
    ],
    "1y-prep": [
      { from: 0,  title: " STAGE 1 — LEARN (full syllabus)", subtitle: "NCERT-first chapter coverage of every PCB chapter (M1-M8)" },
      { from: 8,  title: " STAGE 2 — SLOW REVISION",         subtitle: "Syllabus done. First deep NCERT revision of every chapter (M9-M10)" },
      { from: 10, title: " STAGE 3 — EXAM MODE",             subtitle: "Fast revision + 2-3 mocks/wk + OMR (M11-M12)" },
    ],
    "1y-rev": [
      { from: 0, title: " STAGE 1 — SLOW REVISION (NCERT-deep)", subtitle: "Re-read NCERT + redo all PYQs every chapter (M1-M4)" },
      { from: 4, title: " STAGE 2 — FAST REVISION + WEAK FIX",  subtitle: "Slow done. Condensed notes + speed PYQs every chapter (M5-M7)" },
      { from: 7, title: " STAGE 3 — MOCK MARATHON",             subtitle: "Full mocks + OMR refinement + mock-driven repair (M8-M12)" },
    ],
    "2y-prep": [
      { from: 0,  title: " YEAR 1 — CLASS 11 LEARN",     subtitle: "Build Class 11 base across PCB (M1-M10)", chapterClass: 11 },
      { from: 10, title: " YEAR 1 END — CLASS 11 REV",   subtitle: "Class 11 NCERT revision + first mocks (M11-M12)", chapterClass: 11 },
      { from: 12, title: " YEAR 2 — CLASS 12 LEARN",     subtitle: "Class 12 chapters + Class 11 maintenance via mocks (M13-M20)", chapterClass: 12 },
      { from: 20, title: " STAGE 4 — FULL SYLLABUS REV", subtitle: "Both classes: Slow + Fast revision pass (M21-M22)" },
      { from: 22, title: " STAGE 5 — EXAM MODE",         subtitle: "Mocks + sheets + final precision (M23-M24)" },
    ],
  },
};

const strategyRoadmap = {
  jee: {
    "lm-rev": {
      headline: "Last Month Revision: high-yield mixed practice + mock simulation",
      dailyTarget: "8-10 focused hours/day",
      phases: [
        { label: "Week 1", extra: " 1 mixed paper/day ·  Chapter PYQ blocks ·  Same-day correction" },
        { label: "Week 2", extra: " 3 full mocks/week ·  Fix high-error chapters from W1 mocks" },
        { label: "Week 3", extra: " 4 full mocks/week ·  Strict 3-hr exam slot, attempt strategy lock" },
        { label: "Week 4", extra: " 2 final simulations ·  Sheets only ·  Sleep-cycle lock" },
      ],
    },
    "6m-prep": {
      headline: "6-Month Preparation: complete syllabus + 1 revision + mocks",
      dailyTarget: "5-7 focused hours/day",
      phases: [
        { label: "Month 1", extra: " 2 chapter tests/wk ·  Daily DPP per chapter" },
        { label: "Month 2", extra: " 2 chapter tests/wk ·  DPP + chapter PYQs" },
        { label: "Month 3", extra: " 2 part-tests/wk ·  Chapter PYQs ·  Mid-prep audit" },
        { label: "Month 4", extra: " 2 part-tests/wk ·  PYQs ·  Backlog patch (last 7 days)" },
        { label: "Month 5", extra: " 2 mixed tests + 1 full mock/wk ·  First full revision" },
        { label: "Month 6", extra: " 2-3 full mocks/wk ·  Daily error notebook + sheets" },
      ],
    },
    "6m-rev": {
      headline: "6-Month Revision: 2 full passes + mock mode",
      dailyTarget: "6-8 focused hours/day",
      phases: [
        { label: "Month 1", extra: " 2 mixed tests/wk ·  SLOW: re-derive theory + redo all PYQs" },
        { label: "Month 2", extra: " 2 mixed tests/wk ·  SLOW: continue chapter-wise depth" },
        { label: "Month 3", extra: " 3 part-tests/wk ·  FAST: condensed notes + speed PYQs" },
        { label: "Month 4", extra: " 3 part-tests/wk ·  FAST: weak-topic deep correction" },
        { label: "Month 5", extra: " 2 full mocks/wk ·  Mock-driven weak-chapter focus" },
        { label: "Month 6", extra: " 3 full mocks/wk ·  Sheets only · exam-day rehearsal" },
      ],
    },
    "1y-prep": {
      headline: "1-Year Preparation: learn full syllabus + 2 revisions + mocks",
      dailyTarget: "4-6 hrs weekdays, 7+ on holidays",
      phases: [
        { label: "Month 1",  extra: " 1 chapter test/wk ·  Daily DPP per chapter" },
        { label: "Month 2",  extra: " 1 chapter test/wk ·  Daily DPP" },
        { label: "Month 3",  extra: " 2 chapter tests/wk ·  Chapter PYQs + DPP" },
        { label: "Month 4",  extra: " 2 chapter tests/wk ·  Chapter PYQs + DPP" },
        { label: "Month 5",  extra: " 2 part-tests/wk ·  Mixed PYQs (2 subjects)" },
        { label: "Month 6",  extra: " 2 part-tests/wk ·  Mid-year backlog audit" },
        { label: "Month 7",  extra: " 2 part-tests/wk ·  PYQs all done chapters" },
        { label: "Month 8",  extra: " 2-3 part-tests/wk ·  Syllabus 90%+ closure" },
        { label: "Month 9",  extra: " 1 full mock/wk ·  SLOW REV begins" },
        { label: "Month 10", extra: " 1-2 full mocks/wk ·  SLOW REV continues" },
        { label: "Month 11", extra: " 2 full mocks/wk ·  FAST REV cycle" },
        { label: "Month 12", extra: " 3 full mocks/wk ·  Sheets + exam rhythm" },
      ],
    },
    "1y-rev": {
      headline: "1-Year Revision: 2 deep passes + 5-month mock marathon",
      dailyTarget: "5-7 focused hours/day",
      phases: [
        { label: "Month 1",  extra: " 1 mixed test/wk ·  SLOW: theory re-read + PYQs" },
        { label: "Month 2",  extra: " 2 mixed tests/wk ·  SLOW: derivation + PYQs" },
        { label: "Month 3",  extra: " 2 mixed tests/wk ·  SLOW: rest of syllabus" },
        { label: "Month 4",  extra: " 2 mixed tests/wk ·  SLOW pass complete · audit" },
        { label: "Month 5",  extra: " 3 part-tests/wk ·  FAST: condensed notes + PYQs" },
        { label: "Month 6",  extra: " 3 part-tests/wk ·  FAST: speed-solving" },
        { label: "Month 7",  extra: " 3 part-tests/wk ·  FAST pass complete" },
        { label: "Month 8",  extra: " 1 full mock/wk ·  Weak-area deep-fix" },
        { label: "Month 9",  extra: " 2 full mocks/wk ·  Mock-driven repair" },
        { label: "Month 10", extra: " 2 full mocks/wk ·  PYQ marathon" },
        { label: "Month 11", extra: " 3 full mocks/wk ·  Reduce silly errors" },
        { label: "Month 12", extra: " 3 full mocks/wk ·  Sheets + exam-day rehearsal" },
      ],
    },
    "2y-prep": {
      headline: "2-Year Preparation: Class 11 → Class 12 → Revision → Exam",
      dailyTarget: "3-5 hrs Class 11, 5-7 hrs Class 12",
      phases: [
        { label: "Month 1",  extra: " 1 chapter test/wk ·  Daily DPP · Class 11 start" },
        { label: "Month 2",  extra: " 1 chapter test/wk ·  Daily DPP" },
        { label: "Month 3",  extra: " 1 chapter test/wk ·  DPP + chapter PYQs" },
        { label: "Month 4",  extra: " 2 chapter tests/wk ·  DPP + chapter PYQs" },
        { label: "Month 5",  extra: " 2 chapter tests/wk ·  Mixed PYQs (Class 11)" },
        { label: "Month 6",  extra: " 2 part-tests/wk ·  Class 11 backlog patch" },
        { label: "Month 7",  extra: " 2 part-tests/wk ·  PYQs all done chapters" },
        { label: "Month 8",  extra: " 2 part-tests/wk ·  Chapter PYQs continue" },
        { label: "Month 9",  extra: " 2 part-tests/wk ·  Class 11 syllabus 100%" },
        { label: "Month 10", extra: " 2 part-tests/wk ·  Class 11 first revision begins" },
        { label: "Month 11", extra: " 2 mixed tests/wk ·  Class 11 SLOW REV pass" },
        { label: "Month 12", extra: " 1 full Cl11 mock/wk ·  Class 11 audit complete" },
        { label: "Month 13", extra: " 1 chapter test/wk ·  Class 12 start + Cl11 maintenance" },
        { label: "Month 14", extra: " 1 chapter test/wk ·  Cl12 DPP + weekly Cl11 mock" },
        { label: "Month 15", extra: " 2 chapter tests/wk ·  Cl12 PYQs + Cl11 maintenance" },
        { label: "Month 16", extra: " 2 chapter tests/wk ·  Cl12 PYQs" },
        { label: "Month 17", extra: " 2 part-tests/wk ·  Mixed PYQs (Cl12)" },
        { label: "Month 18", extra: " 2 part-tests/wk ·  Cl12 75% done" },
        { label: "Month 19", extra: " 2 part-tests/wk ·  Cl12 90% done" },
        { label: "Month 20", extra: " 1 full mock/wk ·  Cl12 syllabus 100%" },
        { label: "Month 21", extra: " 2 full mocks/wk ·  Full PCM SLOW REV" },
        { label: "Month 22", extra: " 2-3 full mocks/wk ·  Full PCM FAST REV" },
        { label: "Month 23", extra: " 3 full mocks/wk ·  Mock-driven weak-fix" },
        { label: "Month 24", extra: " 3 full mocks/wk ·  Sheets + exam rhythm" },
      ],
    },
  },
  neet: {
    "lm-rev": {
      headline: "Last Month Revision: high-retention NEET mode + mocks",
      dailyTarget: "8-10 focused hours/day",
      phases: [
        { label: "Week 1", extra: " 2 mixed tests ·  NCERT line revision + HP formulas" },
        { label: "Week 2", extra: " 3 mixed tests/wk ·  Phy weak-topic + Chem drill" },
        { label: "Week 3", extra: " 4 full mocks/wk ·  Strict OMR + 2 PM slot" },
        { label: "Week 4", extra: " 2 final mocks ·  Sheets only ·  Sleep-cycle lock" },
      ],
    },
    "6m-prep": {
      headline: "6-Month Preparation: complete PCB + 1 revision + mocks",
      dailyTarget: "5-7 focused hours/day",
      phases: [
        { label: "Month 1", extra: " 2 chapter tests/wk ·  NCERT line + DPP" },
        { label: "Month 2", extra: " 2 chapter tests/wk ·  NCERT + chapter PYQs" },
        { label: "Month 3", extra: " 2 part-tests/wk ·  Chapter PYQs ·  Mid-prep audit" },
        { label: "Month 4", extra: " 2 part-tests/wk ·  PYQs ·  Backlog patch" },
        { label: "Month 5", extra: " 2 mixed tests + 1 full mock/wk ·  NCERT revision" },
        { label: "Month 6", extra: " 2-3 full mocks/wk ·  OMR + error notebook" },
      ],
    },
    "6m-rev": {
      headline: "6-Month Revision: 2 full PCB passes + mock mode",
      dailyTarget: "6-8 focused hours/day",
      phases: [
        { label: "Month 1", extra: " 2 mixed tests/wk ·  SLOW: NCERT line + redo PYQs" },
        { label: "Month 2", extra: " 2 mixed tests/wk ·  SLOW: continue NCERT depth" },
        { label: "Month 3", extra: " 3 part-tests/wk ·  FAST: condensed Bio + speed PYQs" },
        { label: "Month 4", extra: " 3 part-tests/wk ·  FAST: weak-chapter correction" },
        { label: "Month 5", extra: " 2 full mocks/wk ·  Mock-driven weak-chapter focus" },
        { label: "Month 6", extra: " 3 full mocks/wk ·  NCERT skim + exam rehearsal" },
      ],
    },
    "1y-prep": {
      headline: "1-Year Preparation: learn full PCB + 2 revisions + mocks",
      dailyTarget: "4-6 hrs weekdays, 7+ on holidays",
      phases: [
        { label: "Month 1",  extra: " 1 chapter test/wk ·  NCERT line + DPP" },
        { label: "Month 2",  extra: " 1 chapter test/wk ·  NCERT + DPP" },
        { label: "Month 3",  extra: " 2 chapter tests/wk ·  Chapter PYQs + NCERT" },
        { label: "Month 4",  extra: " 2 chapter tests/wk ·  Chapter PYQs" },
        { label: "Month 5",  extra: " 2 part-tests/wk ·  Mixed PYQs (2 subjects)" },
        { label: "Month 6",  extra: " 2 part-tests/wk ·  Mid-year backlog audit" },
        { label: "Month 7",  extra: " 2 part-tests/wk ·  PYQs all done chapters" },
        { label: "Month 8",  extra: " 2-3 part-tests/wk ·  Syllabus 90%+ closure" },
        { label: "Month 9",  extra: " 1 full mock/wk ·  NCERT SLOW REV begins" },
        { label: "Month 10", extra: " 1-2 full mocks/wk ·  NCERT SLOW REV continues" },
        { label: "Month 11", extra: " 2 full mocks/wk ·  FAST REV + OMR" },
        { label: "Month 12", extra: " 3 full mocks/wk ·  Sheets + exam rhythm" },
      ],
    },
    "1y-rev": {
      headline: "1-Year Revision: 2 deep PCB passes + 5-month mock marathon",
      dailyTarget: "5-7 focused hours/day",
      phases: [
        { label: "Month 1",  extra: " 1 mixed test/wk ·  SLOW: NCERT line + PYQs" },
        { label: "Month 2",  extra: " 2 mixed tests/wk ·  SLOW: NCERT depth + PYQs" },
        { label: "Month 3",  extra: " 2 mixed tests/wk ·  SLOW: rest of syllabus" },
        { label: "Month 4",  extra: " 2 mixed tests/wk ·  SLOW pass complete · audit" },
        { label: "Month 5",  extra: " 3 part-tests/wk ·  FAST: condensed Bio + PYQs" },
        { label: "Month 6",  extra: " 3 part-tests/wk ·  FAST: speed-solving" },
        { label: "Month 7",  extra: " 3 part-tests/wk ·  FAST pass complete" },
        { label: "Month 8",  extra: " 1 full mock/wk ·  Weak-chapter deep-fix" },
        { label: "Month 9",  extra: " 2 full mocks/wk ·  Mock-driven repair" },
        { label: "Month 10", extra: " 2 full mocks/wk ·  PYQ marathon (10-yr NEET)" },
        { label: "Month 11", extra: " 3 full mocks/wk ·  Reduce negative marking" },
        { label: "Month 12", extra: " 3 full mocks/wk ·  Sheets + exam-day rehearsal" },
      ],
    },
    "2y-prep": {
      headline: "2-Year Preparation: Class 11 → Class 12 → Revision → Exam",
      dailyTarget: "3-5 hrs Class 11, 5-7 hrs Class 12",
      phases: [
        { label: "Month 1",  extra: " 1 chapter test/wk ·  NCERT + DPP · Class 11 start" },
        { label: "Month 2",  extra: " 1 chapter test/wk ·  NCERT + DPP" },
        { label: "Month 3",  extra: " 1 chapter test/wk ·  NCERT + chapter PYQs" },
        { label: "Month 4",  extra: " 2 chapter tests/wk ·  Chapter PYQs + NCERT" },
        { label: "Month 5",  extra: " 2 chapter tests/wk ·  Mixed PYQs (Class 11)" },
        { label: "Month 6",  extra: " 2 part-tests/wk ·  Class 11 backlog patch" },
        { label: "Month 7",  extra: " 2 part-tests/wk ·  PYQs done chapters" },
        { label: "Month 8",  extra: " 2 part-tests/wk ·  Chapter PYQs continue" },
        { label: "Month 9",  extra: " 2 part-tests/wk ·  Class 11 syllabus 100%" },
        { label: "Month 10", extra: " 2 part-tests/wk ·  Class 11 first revision begins" },
        { label: "Month 11", extra: " 2 mixed tests/wk ·  Class 11 NCERT SLOW REV" },
        { label: "Month 12", extra: " 1 full Cl11 mock/wk ·  Class 11 audit complete" },
        { label: "Month 13", extra: " 1 chapter test/wk ·  Class 12 start + Cl11 maintenance" },
        { label: "Month 14", extra: " 1 chapter test/wk ·  Cl12 DPP + weekly Cl11 mock" },
        { label: "Month 15", extra: " 2 chapter tests/wk ·  Cl12 PYQs + Cl11 maintenance" },
        { label: "Month 16", extra: " 2 chapter tests/wk ·  Cl12 PYQs" },
        { label: "Month 17", extra: " 2 part-tests/wk ·  Mixed PYQs (Cl12)" },
        { label: "Month 18", extra: " 2 part-tests/wk ·  Cl12 75% done" },
        { label: "Month 19", extra: " 2 part-tests/wk ·  Cl12 90% done" },
        { label: "Month 20", extra: " 1 full mock/wk ·  Cl12 syllabus 100%" },
        { label: "Month 21", extra: " 2 full mocks/wk ·  Full PCB SLOW REV" },
        { label: "Month 22", extra: " 2-3 full mocks/wk ·  Full PCB FAST REV" },
        { label: "Month 23", extra: " 3 full mocks/wk ·  Mock-driven weak-fix" },
        { label: "Month 24", extra: " 3 full mocks/wk ·  Sheets + exam rhythm" },
      ],
    },
  },
};
// ─── Component ───────────────────────────────────────────────────────────────
export default function ExamInfo() {
  const [activeExam, setActiveExam] = useState("jee-mains");
  const [activeTab, setActiveTab] = useState("overview");
  const [examDropOpen, setExamDropOpen] = useState(false);
  const [syllabusSubTab, setSyllabusSubTab] = useState("");
  const [strategyExam, setStrategyExam] = useState("jee");
  const [strategyDuration, setStrategyDuration] = useState("1y-prep");

  const dropRef = useRef(null);
  useEffect(() => {
    const handler = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setExamDropOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    setStrategyExam(activeExam === "neet" ? "neet" : "jee");
  }, [activeExam]);

  const openTab = (t) => { setActiveTab(t); };
  const openExam = (id) => { setActiveExam(id); setActiveTab("overview"); setExamDropOpen(false); setSyllabusSubTab(""); };

  const exam = exams.find(e => e.id === activeExam);
  const selectedRoadmap = strategyRoadmap[strategyExam][strategyDuration];
  const selectedDurationMeta = strategyDurationMeta[strategyDuration];
  const stagesForCurrent = strategyStages[strategyExam]?.[strategyDuration] || [];
  const strategyTableRows = useMemo(
    () => buildStrategyRowsByDuration(strategyExam, strategyDuration),
    [strategyExam, strategyDuration],
  );
  const strategySubjects = strategyExam === "jee" ? ["Mathematics", "Chemistry", "Physics"] : ["Biology", "Chemistry", "Physics"];
  const totalChaptersCovered = strategySubjects.reduce(
    (total, subject) => total + (strategySyllabusByExam[strategyExam][subject]?.length || 0),
    0,
  );

  return (
    <div className={styles.page}>

      {/* ── PAGE HERO ── */}
      <section className={styles.pageHero}>
        <div className={styles.pageHeroInner}>
          <p className={styles.pageHeroEyebrow}>Exam Guide &middot; {academicYear}</p>
          <h1 className={styles.pageHeroTitle}>
            JEE &amp; NEET&nbsp;<span className={styles.pageHeroAccent}>Exam Guide</span>
          </h1>
          <p className={styles.pageHeroSub}>
            Complete overview of JEE Mains, JEE Advanced &amp; NEET UG &mdash; patterns, syllabus, dates &amp; preparation strategy.
          </p>
        </div>
      </section>

      {/* ── SINGLE-ROW TOPBAR ────────────────────────────────── */}
      <div className={styles.topBar}>
        <div className={styles.topBarInner}>

          {/* LEFT: breadcrumb */}
          <div className={styles.topBarLeft}>
            <span className={styles.topBarIcon}></span>
            <span className={styles.topBarTitle}>Exam Guide</span>
            <span className={styles.topBarSep}>/</span>
            <span className={styles.topBarSub}>JEE &amp; NEET {academicYear}</span>
          </div>

          {/* CENTER: section tabs */}
          <div className={styles.topBarTabs}>
            {[
              { id: "overview", label: "Overview" },
              { id: "strategy", label: "Strategy" },
              { id: "pattern",  label: "Pattern" },
              { id: "dates",    label: "Dates" },
              { id: "syllabus", label: "Syllabus" },
            ].map(t => (
              <button key={t.id}
                className={`${styles.topBarTabBtn} ${activeTab === t.id ? styles.topBarTabBtnActive : ""}`}
                style={{ "--exam-color": ["strategy"].includes(t.id) ? "#f59e0b" : exam.color }}
                onClick={() => openTab(t.id)}>
                {t.label}
              </button>
            ))}
          </div>

          {/* RIGHT: exam dropdown */}
          <div className={styles.examDropWrapper} ref={dropRef}>
            <button
              className={styles.examDropTrigger}
              style={{ "--exam-color": exam.color }}
              onClick={() => setExamDropOpen(o => !o)}>
              <span className={styles.examDropIcon}>{exam.icon}</span>
              <span className={styles.examDropName}>{exam.title}</span>
              <span className={`${styles.examDropChevron} ${examDropOpen ? styles.examDropChevronOpen : ""}`}>▾</span>
            </button>
            {examDropOpen && (
              <div className={styles.examDropMenu}>
                {exams.map(e => (
                  <button key={e.id}
                    className={`${styles.examDropItem} ${activeExam === e.id ? styles.examDropItemActive : ""}`}
                    style={{ "--exam-color": e.color }}
                    onClick={() => openExam(e.id)}>
                    <span className={styles.examDropItemIcon}>{e.icon}</span>
                    <span className={styles.examDropItemMeta}>
                      <span className={styles.examDropItemName}>{e.title}</span>
                      <span className={styles.examDropItemTag}>{e.tag}</span>
                    </span>
                    {activeExam === e.id && <span className={styles.examDropItemCheck}>✓</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── LAYOUT ─────────────────────────────────────────── */}
      <div className={styles.layout}>

        {/* MAIN CONTENT */}
        <main id="exam-detail" className={styles.mainContent}>

          {/* ── OVERVIEW ── */}
          {activeTab === "overview" && (
            <div className={styles.tabContent}>
              {/* Conducted-by info bar */}
              <div className={styles.conductedBar} style={{ "--exam-color": exam.color }}>
                <div className={styles.conductedLeft}>
                  <span className={styles.conductedIcon}>{exam.icon}</span>
                  <div>
                    <p className={styles.conductedSub}>{exam.tag} Entrance Exam</p>
                    <h2 className={styles.conductedTitle}>{exam.title}</h2>
                    <p className={styles.conductedSub2}>{exam.subtitle}</p>
                    <p className={styles.conductedDesc}>{exam.shortDesc}</p>
                  </div>
                </div>
                <div className={styles.conductedRight}>
                  <span className={styles.conductedByLabel}>Conducted by</span>
                  <strong className={styles.conductedOrg}>{exam.conducted}</strong>
                  <a href={exam.officialSite} target="_blank" rel="noopener noreferrer"
                    className={styles.officialBtn} style={{ "--exam-color": exam.color }}>
                     Official Website
                  </a>
                </div>
              </div>
              {/* Exam-specific stat strip */}
              {exam.stats && (
                <div className={styles.overviewStats} style={{ "--exam-color": exam.color }}>
                  {exam.stats.map((s, i) => (
                    <React.Fragment key={s.lbl}>
                      <div className={styles.ovStat}>
                        <span className={styles.ovStatNum} style={{ color: exam.color }}>{s.num}</span>
                        <span className={styles.ovStatLbl}>{s.lbl}</span>
                      </div>
                      {i < exam.stats.length - 1 && <div className={styles.ovStatDivider} />}
                    </React.Fragment>
                  ))}
                </div>
              )}
              <div className={styles.tableWrapper}>
                <table className={styles.keyFactsTable}>
                  <thead>
                    <tr>
                      {exam.keyFacts.map(f => (
                        <th key={f.label} className={styles.kfColHead} style={{ color: exam.color }}>
                          <span className={styles.kfColIcon}>{f.icon}</span>
                          <span>{f.label}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {exam.keyFacts.map(f => (
                        <td key={f.label} className={styles.kfColValue}>{f.value}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className={styles.markingBox} style={{ borderColor: exam.color }}>
                <h4 className={styles.markingTitle} style={{ color: exam.color }}> Marking Scheme</h4>
                <p className={styles.markingText}>{exam.pattern.markingScheme}</p>
              </div>
              <div className={styles.normBox}>
                <h4 className={styles.normTitle}> Normalization / Score Policy</h4>
                <p className={styles.normText}>{exam.pattern.normalization}</p>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.compareTable}>
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th style={{ color: "#2563eb" }}>︁EJEE Mains</th>
                      <th style={{ color: "#7c3aed" }}> JEE Advanced</th>
                      <th style={{ color: "#059669" }}> NEET UG</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? styles.rowEven : ""}>
                        <td><strong>{row.param}</strong></td>
                        <td>{row.jeeMain}</td>
                        <td>{row.jeeAdv}</td>
                        <td>{row.neet}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.cta}>
                <a href="https://testseries.prrepxpathshala.live/test-series/pyq" target="_blank" rel="noopener noreferrer"
                  className={styles.ctaBtn} style={{ background: exam.gradient }}>
                   Practice Free Mock Tests
                </a>
                <a href={exam.officialSite} target="_blank" rel="noopener noreferrer"
                  className={styles.ctaBtnOutline} style={{ color: exam.color, borderColor: exam.color }}>
                   Official Notification
                </a>
              </div>
            </div>
          )}

          {/* ── PATTERN ── */}
          {activeTab === "pattern" && (
            <div className={styles.tabContent}>
              {exam.pattern.papers.map(paper => (
                <div key={paper.name} className={styles.paperCard}>
                  <div className={styles.paperHeader} style={{ background: exam.gradient }}>
                    <h3 className={styles.paperName}>{paper.name}</h3>
                    <span className={styles.paperTotal}>{paper.total}</span>
                  </div>
                  <div className={styles.tableWrapper}>
                    <table className={styles.patternTable}>
                      <thead>
                        <tr>
                          <th>Subject</th>
                          <th>MCQ / Questions</th>
                          <th>Integer / Numerical</th>
                          <th>Marks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paper.sections.map(s => (
                          <tr key={s.subject}>
                            <td><strong>{s.subject}</strong></td>
                            <td>{s.mcq}</td>
                            <td>{s.numerical}</td>
                            <td>{s.marks}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              <div className={styles.markingBox} style={{ borderColor: exam.color }}>
                <h4 className={styles.markingTitle} style={{ color: exam.color }}> Marking Scheme</h4>
                <p className={styles.markingText}>{exam.pattern.markingScheme}</p>
              </div>
              {exam.pattern.normalization && (
                <div className={styles.markingBox} style={{ borderColor: exam.color }}>
                  <h4 className={styles.markingTitle} style={{ color: exam.color }}> Normalization &amp; Ranking</h4>
                  <p className={styles.markingText}>{exam.pattern.normalization}</p>
                </div>
              )}
              {exam.pattern.trendCurrent && (
                <div className={styles.markingBox} style={{ borderColor: exam.color }}>
                  <h4 className={styles.markingTitle} style={{ color: exam.color }}> {examYear} Trend &amp; Recent Changes</h4>
                  <ul className={styles.strategyList} style={{ marginTop: 6 }}>
                    {exam.pattern.trendCurrent.map(t => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* ── SYLLABUS ── */}
          {activeTab === "syllabus" && (() => {
            // Pick the right dataset based on active exam
            const fullData = exam.id === "neet" ? neetSyllabusData : jeeSyllabusData;
            const subjects = Object.keys(fullData);
            const activeSub = syllabusSubTab || subjects[0];
            // label for JEE Adv differs slightly
            const examLabel = exam.id === "jee-advanced" ? `JEE Adv ${examYear}` : exam.id === "neet" ? `NEET ${examYear}` : `JEE Mains ${examYear}`;
            return (
              <div className={styles.tabContent}>
                <p className={styles.syllabusNote}>
                   Syllabus aligned with NCERT Class 11 &amp; 12. Always verify with the{' '}
                  <a href={exam.officialSite} target="_blank" rel="noopener noreferrer" style={{ color: exam.color }}>official notification</a>{' '}
                  for the latest updates.
                </p>

                {/* Subject sub-tabs */}
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                  {subjects.map(sub => {
                    const active = activeSub === sub;
                    return (
                      <button key={sub} onClick={() => setSyllabusSubTab(sub)} style={{
                        padding: '9px 24px',
                        border: `2px solid ${active ? exam.color : '#e2e8f0'}`,
                        borderRadius: 100,
                        background: active ? exam.color : '#fff',
                        color: active ? '#fff' : '#64748b',
                        fontWeight: 700, fontSize: '0.87rem',
                        cursor: 'pointer', transition: 'all 0.18s', fontFamily: 'inherit',
                      }}>
                        {sub.charAt(0).toUpperCase() + sub.slice(1)} ({fullData[sub].length})
                      </button>
                    );
                  })}
                </div>

                {/* Full chapter table */}
                <SyllabusTable
                  data={fullData[activeSub]}
                  examLabel={examLabel}
                  accentColor={exam.color}
                />

                {/* Board syllabus links */}
                <div style={{ marginTop: 24, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                  <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.8px' }}>
                    Also explore board syllabuses
                  </p>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: 'center' }}>
                    <Link to="/cbse12th-syllabus" style={{
                      padding: '10px 22px', border: '1.5px solid #e2e8f0', borderRadius: 100,
                      background: '#fff', color: '#334155', fontWeight: 700, fontSize: '0.84rem',
                      textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                    }}> Class 12 Board Syllabus</Link>
                    <Link to="/cbse10th-syllabus" style={{
                      padding: '10px 22px', border: '1.5px solid #e2e8f0', borderRadius: 100,
                      background: '#fff', color: '#334155', fontWeight: 700, fontSize: '0.84rem',
                      textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.07)',
                    }}> Class 10 Board Syllabus</Link>
                  </div>
                </div>
              </div>
            );
          })()}

          {/* ── DATES ── */}
          {activeTab === "dates" && (
            <div className={styles.tabContent}>
              <div className={styles.approxBanner}>
                <strong>Note:</strong> All dates below are <strong>approximate</strong> and based on historical trends. Actual dates may vary. Always check the <a href={exam.officialSite} target="_blank" rel="noopener noreferrer" style={{ color: exam.color }}>official website</a> for confirmed dates.
              </div>
              <div className={styles.timelineWrapper}>
                {exam.importantDates.map((d, i) => (
                  <div key={i} className={styles.timelineItem} style={{ "--exam-color": exam.color }}>
                    <div className={styles.timelineDot} style={{ background: exam.gradient }} />
                    <div className={styles.timelineCard}>
                      <p className={styles.timelineEvent}>{d.event}</p>
                      <p className={styles.timelineDate} style={{ color: exam.color }}>{d.date}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.cutoffBox} style={{ borderColor: exam.color }}>
                <h4 className={styles.cutoffTitle} style={{ color: exam.color }}> Cutoff Trends</h4>
                <p className={styles.cutoffText}>{exam.cutoff}</p>
              </div>
              <p className={styles.dateDisclaimer}>
                * Dates are approximate based on historical trends for {academicYear}. Always verify on the official website.
              </p>
            </div>
          )}

          {/* ── PREPARATION STRATEGY ── */}
          {activeTab === "strategy" && (
            <div className={styles.tabContent}>
              <div className={styles.strategyHeaderRow}>
                <div className={styles.strategyHeadingBlock}>
                  <span className={styles.tabPageBadge}> Preparation Strategy</span>
                  <div className={styles.strategyLegendBar}>
                    <span>[HP] = High priority</span>
                    <span>[MP] = Medium priority</span>
                    <span>[LP] = Low priority</span>
                  </div>
                </div>

                <div className={styles.strategyTopControls}>
                  <div className={`${styles.strategyTopControl} ${styles.strategyTopControlExam}`}>
                    <label htmlFor="strategy-exam" className={styles.strategyControlLabel}>Exam</label>
                    <select
                      id="strategy-exam"
                      value={strategyExam}
                      onChange={(e) => setStrategyExam(e.target.value)}
                      className={styles.strategySelect}
                    >
                      <option value="jee">JEE</option>
                      <option value="neet">NEET</option>
                    </select>
                  </div>

                  <div className={`${styles.strategyTopControl} ${styles.strategyTopControlDuration}`}>
                    <label htmlFor="strategy-duration" className={styles.strategyControlLabel}>Duration</label>
                    <select
                      id="strategy-duration"
                      value={strategyDuration}
                      onChange={(e) => setStrategyDuration(e.target.value)}
                      className={styles.strategySelect}
                    >
                      {strategyDurationOptions.map((option) => (
                        <option key={option.id} value={option.id}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className={styles.strategyTableTitleCard}>
                <h3 className={styles.strategyTableTitle}>{selectedDurationMeta.table1Title}</h3>
                <p className={styles.strategyTableSub}>Daily Target: {selectedRoadmap.dailyTarget} | {selectedDurationMeta.table1Focus} | Coverage: {totalChaptersCovered} chapters</p>
              </div>

              <div className={styles.tableWrapper}>
                <table className={styles.strategyMainTable}>
                  <thead>
                    <tr>
                      <th>Time</th>
                      {strategySubjects.map((subject) => (
                        <th key={subject}>{subject} (HP/MP/LP)</th>
                      ))}
                      <th>Tests / PYQ / Focus</th>
                    </tr>
                  </thead>
                  <tbody>
                    {strategyTableRows.map((row, rowIdx) => (
                      <React.Fragment key={row.phase}>
                        {stagesForCurrent.filter((s) => s.from === rowIdx).map((s) => (
                          <tr key={`stg1-${s.title}`} className={styles.strategyStageRow}>
                            <td colSpan={strategySubjects.length + 2}>
                              <span className={styles.strategyStageTitle}>{s.title}</span>
                              <span className={styles.strategyStageSub}> — {s.subtitle}</span>
                            </td>
                          </tr>
                        ))}
                        <tr>
                          <td><strong>{row.phase}</strong></td>
                          {strategySubjects.map((subject) => (
                            <td key={`${row.phase}-${subject}`}>{row[subject]}</td>
                          ))}
                          <td>{row.extra}</td>
                        </tr>
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={styles.strategyTrendCard}>
                <h4 className={styles.strategySectionTitle}>Latest Trend Snapshot (last 5 yrs NTA data)</h4>
                <ul className={styles.strategyList}>
                  {strategyTrendInsights[strategyExam].map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className={styles.strategyTrendCard}>
                <h4 className={styles.strategySectionTitle}>Core Topper Principles (universal — apply daily)</h4>
                <ul className={styles.strategyList}>
                  {strategyCorePrinciples.map((p) => (
                    <li key={p.title}><strong>{p.title}:</strong> {p.detail}</li>
                  ))}
                </ul>
              </div>

              <div className={styles.strategyTrendCard}>
                <h4 className={styles.strategySectionTitle}>Mock Test &amp; Analysis Protocol (single biggest score multiplier)</h4>
                <ul className={styles.strategyList}>
                  {strategyMockProtocol.map((p) => (
                    <li key={p.title}><strong>{p.title}:</strong> {p.detail}</li>
                  ))}
                </ul>
              </div>

              <div className={styles.strategyTrendCard}>
                <h4 className={styles.strategySectionTitle}>Last 30 Days Protocol ({strategyExam.toUpperCase()}) — non-negotiable rules</h4>
                <ul className={styles.strategyList}>
                  {strategyLast30Days[strategyExam].map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>

              <div className={styles.strategyTrendCard}>
                <h4 className={styles.strategySectionTitle}>Common Pitfalls That Kill Rank (avoid all 10)</h4>
                <ul className={styles.strategyList}>
                  {strategyPitfalls.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}

        </main>
      </div>

      {/* ── CTA BANNER ───────────────────────────────────── */}
      <section className={styles.ctaBanner}>
        <div className={styles.ctaBannerInner}>
          <h2 className={styles.ctaBannerTitle}>Ready to Start Practice?</h2>
          <p className={styles.ctaBannerSub}>PrrepX Test - One Subscription, Complete Access Model: 471+ Premium Scheduled Tests, Unlimited Open Zone Tests &amp; 80+ Free PYQ Tests.</p>
          <div className={styles.ctaBannerBtns}>
            <a href="https://testseries.prrepxpathshala.live/test-series/pyq" target="_blank" rel="noopener noreferrer" className={styles.ctaBannerBtn}> Free PYQ Tests</a>
            <a href="https://testseries.prrepxpathshala.live/test-series/jee-premium" target="_blank" rel="noopener noreferrer" className={styles.ctaBannerBtnSecondary}> JEE Premium Tests</a>
            <a href="https://testseries.prrepxpathshala.live/test-series/neet-premium" target="_blank" rel="noopener noreferrer" className={styles.ctaBannerBtnSecondary}> NEET Premium Tests</a>
          </div>
        </div>
      </section>

      {/* ── FOOTER NOTE ──────────────────────────────────── */}
      <div className={styles.footerNote}>
        <p> {currentYear} PrrepX Pathshala · All information is for {academicYear} academic cycle · Always verify with official sources</p>
      </div>

    </div>
  );
}
